import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PhoneCall, CheckCircle2, Loader2, Megaphone } from 'lucide-react';
import { Card, Empty } from '../ui/Card.jsx';
import { fetchSurveyCampaigns, fetchCampaignStats } from '../../api/survey.js';

/**
 * CampaignStatsPanel — Campaign 撥打表現 tab
 *
 * 顯示該 avatar 有的所有 campaigns，每張 card 顯示：
 *   - 名稱 / 狀態
 *   - 撥打次數 / 接通率 / 答完率 / 完成率
 *
 * 點 card → /outbound/:campaignId（既有的 OutboundCampaignDetailPage）
 */
export function CampaignStatsPanel({ avatarId, hostId }) {
    const { t } = useTranslation('survey');
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!hostId || !avatarId) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const r = await fetchSurveyCampaigns(avatarId, hostId);
                if (!cancelled) setCampaigns(r.campaigns || []);
            } catch (e) {
                if (!cancelled) setError(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [avatarId, hostId]);

    if (loading) return <div className="text-sm text-ink-400 italic">{t('campaign.loading')}</div>;
    if (error) return <div className="text-sm text-danger-500">{error.message}</div>;
    if (!campaigns.length) {
        return (
            <Card>
                <Empty
                    icon={Megaphone}
                    title={t('campaign.emptyTitle')}
                    description={t('campaign.emptyDesc')}
                />
            </Card>
        );
    }

    return (
        <div className="space-y-3">
            {campaigns.map((c) => (
                <CampaignCard key={c.id} campaign={c} avatarId={avatarId} hostId={hostId} />
            ))}
        </div>
    );
}

function CampaignCard({ campaign, avatarId, hostId }) {
    const { t } = useTranslation('survey');
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await fetchCampaignStats(avatarId, hostId, campaign.id);
                if (!cancelled) setStats(r);
            } catch (e) {
                if (!cancelled) console.error('campaign-stats error:', e.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [avatarId, hostId, campaign.id]);

    const dial = stats?.dialing || {};
    const sur = stats?.survey || {};

    return (
        <Card
            padded={false}
            className="hover:border-accent-300 transition cursor-pointer"
        >
            <div className="px-5 py-4" onClick={() => navigate(`/outbound/${campaign.id}`)}>
                <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                        <div className="flex items-center gap-2">
                            <h3 className="text-base font-semibold text-ink-900">{campaign.name}</h3>
                            <StatusBadge status={campaign.status} />
                            {campaign.isFake && (
                                <span className="inline-flex px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-800 rounded">FAKE</span>
                            )}
                        </div>
                        <div className="mono-caption mt-0.5">
                            {campaign.id.slice(0, 12)}…
                            {campaign.startedAt && ` · ${t('campaign.startedAt', { date: new Date(campaign.startedAt).toLocaleDateString('zh-TW') })}`}
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="text-xs text-ink-400 italic">{t('campaign.loadingStats')}</div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Metric
                            icon={PhoneCall}
                            label={t('campaign.callsAttempted')}
                            value={dial.callsAttempted ?? 0}
                            sub={t('campaign.contactsSub', { n: dial.totalContacts ?? 0 })}
                        />
                        <Metric
                            icon={CheckCircle2}
                            label={t('campaign.answerRate')}
                            value={`${dial.answerRate ?? 0}%`}
                            sub={t('campaign.answeredSub', { n: dial.answeredContacts ?? 0 })}
                            tone="emerald"
                        />
                        <Metric
                            icon={Loader2}
                            label={t('campaign.completionRate')}
                            value={`${sur.contactsCompletionRate ?? 0}%`}
                            sub={t('campaign.completedSub', { done: sur.contactsCompleted ?? 0, total: dial.totalContacts ?? 0 })}
                            tone="accent"
                        />
                        <Metric
                            icon={Loader2}
                            label={t('campaign.sessionCompletionRate')}
                            value={`${sur.sessionCompletionRate ?? 0}%`}
                            sub={t('campaign.sessionSub', { done: sur.sessionsCompleted ?? 0, total: sur.sessionsStarted ?? 0 })}
                        />
                    </div>
                )}
            </div>
        </Card>
    );
}

function Metric({ icon: Icon, label, value, sub, tone }) {
    const toneMap = {
        accent: 'text-accent-700',
        emerald: 'text-emerald-600',
        default: 'text-ink-900',
    };
    return (
        <div className="bg-cream-50 rounded-md p-3 border border-cream-200">
            <div className="flex items-center gap-1.5 mono-caption mb-1">
                {Icon && <Icon size={11} className="text-ink-400" />}
                {label}
            </div>
            <div className={['text-lg font-semibold tnum', toneMap[tone] || toneMap.default].join(' ')}>{value}</div>
            <div className="text-[11px] text-ink-400">{sub}</div>
        </div>
    );
}

function StatusBadge({ status }) {
    const { t } = useTranslation('survey');
    const meta = {
        running: { label: t('status.running'), cls: 'bg-emerald-100 text-emerald-700' },
        scheduled: { label: t('status.scheduled'), cls: 'bg-blue-100 text-blue-700' },
        paused: { label: t('status.paused'), cls: 'bg-amber-100 text-amber-800' },
        done: { label: t('status.done'), cls: 'bg-cream-200 text-ink-700' },
        draft: { label: t('status.draft'), cls: 'bg-cream-100 text-ink-500' },
        archived: { label: t('status.archived'), cls: 'bg-cream-100 text-ink-400' },
    }[status] || { label: status || '—', cls: 'bg-cream-100 text-ink-500' };
    return (
        <span className={['inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded', meta.cls].join(' ')}>
            {meta.label}
        </span>
    );
}
